import React, { useEffect, useState } from "react";
import { apiClient, formatSAR } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { Wallet, Plus } from "lucide-react";

export default function PayoutsTab() {
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [form, setForm] = useState({ dealer_id: "", amount_sar: 0, iban: "", note: "" });

  const load = () => apiClient.get("/admin/payouts").then(({ data }) => setItems(data.items || []));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    apiClient.get("/admin/users", { params: { role: "dealer" } }).then(({ data }) => setDealers(data.items || []));
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.dealer_id || !form.amount_sar) return;
    await apiClient.post("/admin/payouts", form);
    setForm({ dealer_id: "", amount_sar: 0, iban: "", note: "" });
    load();
  };

  const setStatus = async (id, status) => {
    await apiClient.patch(`/admin/payouts/${id}`, { status });
    load();
  };

  const totals = items.reduce((acc, p) => {
    acc[p.status] = (acc[p.status] || 0) + (p.amount_sar || 0);
    return acc;
  }, {});

  return (
    <div data-testid="admin-payouts" className="space-y-6">
      <h2 className="font-display text-2xl flex items-center gap-2"><Wallet className="w-5 h-5 text-[#8a6f2e]" /> {lang === "ar" ? "تحويلات التجار" : "Dealer payouts"} ({items.length})</h2>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {["pending", "approved", "paid", "rejected"].map(s => (
          <div key={s} className="surface rounded-xl p-4">
            <div className="text-xs text-[#6b6b6b] uppercase tracking-widest">{s}</div>
            <div className="font-display text-xl mt-1">{formatSAR(totals[s] || 0)} <span className="text-[10px] text-[#8a8a8a]">SAR</span></div>
          </div>
        ))}
      </div>

      <form onSubmit={create} className="surface rounded-xl p-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <select className="input-dark sm:col-span-2" value={form.dealer_id} onChange={(e) => setForm({ ...form, dealer_id: e.target.value })} required data-testid="payout-dealer">
          <option value="">{lang === "ar" ? "اختر تاجراً" : "Select dealer"}</option>
          {dealers.map(d => <option key={d.id} value={d.id}>{d.company_name || d.name} — {d.email}</option>)}
        </select>
        <input className="input-dark" type="number" step="0.01" placeholder={lang === "ar" ? "المبلغ (SAR)" : "Amount (SAR)"} value={form.amount_sar} onChange={(e) => setForm({ ...form, amount_sar: Number(e.target.value) })} required data-testid="payout-amount" />
        <input className="input-dark" placeholder="IBAN" value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} data-testid="payout-iban" />
        <input className="input-dark sm:col-span-3" placeholder={lang === "ar" ? "ملاحظة" : "Note"} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="payout-note" />
        <button type="submit" className="btn-primary" data-testid="payout-create"><Plus className="w-4 h-4" /> {lang === "ar" ? "تسجيل تحويل" : "Add payout"}</button>
      </form>

      <div className="surface rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0a0a]/[0.04] text-[#3a3a3a] text-xs uppercase tracking-widest">
            <tr>
              <th className="p-3 text-start">{lang === "ar" ? "التاريخ" : "Date"}</th>
              <th className="p-3 text-start">{lang === "ar" ? "التاجر" : "Dealer"}</th>
              <th className="p-3 text-end">{lang === "ar" ? "المبلغ" : "Amount"}</th>
              <th className="p-3 text-start">IBAN</th>
              <th className="p-3 text-start">{lang === "ar" ? "الحالة" : "Status"}</th>
              <th className="p-3 text-end">{lang === "ar" ? "إجراء" : "Action"}</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan="6" className="p-12 text-center text-[#6b6b6b]">{lang === "ar" ? "لا تحويلات بعد." : "No payouts yet."}</td></tr>
            ) : items.map(p => (
              <tr key={p.id} className="border-t border-[#0a0a0a]/[0.06]" data-testid={`payout-${p.id}`}>
                <td className="p-3 text-xs text-[#3a3a3a]">{new Date(p.created_at).toLocaleString()}</td>
                <td className="p-3"><div className="text-sm">{p.dealer_name || p.dealer_id?.slice(0, 8)}</div><div className="text-xs text-[#8a8a8a]">{p.dealer_email}</div></td>
                <td className="p-3 text-end font-medium">{formatSAR(p.amount_sar)}</td>
                <td className="p-3 text-xs font-mono text-[#3a3a3a]">{p.iban || "—"}</td>
                <td className="p-3"><span className={`pill text-[10px] ${p.status === "paid" ? "pill-accent" : ""}`}>{p.status}</span></td>
                <td className="p-3">
                  <div className="flex justify-end gap-1 flex-wrap">
                    {["pending", "approved", "paid", "rejected"].map(s => (
                      <button key={s} onClick={() => setStatus(p.id, s)} className={`btn-ghost text-[10px] ${p.status === s ? "border-[#c9a55c]" : ""}`} data-testid={`payout-${p.id}-set-${s}`}>{s}</button>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
